import OpenAI from 'openai';

// Initialize OpenAI client
const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY || '',
  dangerouslyAllowBrowser: true // Only for demo purposes
});

export interface CodexSuggestion {
  type: 'material' | 'pricing' | 'optimization' | 'safety';
  title: string;
  description: string;
  action?: string;
  priority: 'low' | 'medium' | 'high';
}

export interface CodexAnalysis {
  materialRecommendations: string[];
  pricingSuggestions: string[];
  safetyWarnings: string[];
  optimizationTips: string[];
  estimatedSavings?: number;
}

export interface CodexJobOptimization {
  suggestedLength: number;
  wasteReduction: number;
  costSavings: number;
  alternativeMaterials: Array<{
    name: string;
    cost: number;
    savings: number;
  }>;
}

class CodexService {
  private isAvailable(): boolean {
    return !!import.meta.env.VITE_OPENAI_API_KEY;
  }

  async analyzeJob(material: string, length: number, quantity: number, customerName?: string): Promise<CodexAnalysis> {
    if (!this.isAvailable()) {
      return this.getMockAnalysis();
    }

    try {
      const prompt = `Analyze this cutting job for a hardware store:
Material: ${material}
Length: ${length} feet
Quantity: ${quantity} pieces
Customer: ${customerName || 'Not specified'}

Provide recommendations for:
1. Material alternatives and optimizations
2. Pricing strategies
3. Safety considerations
4. Cost-saving opportunities

Format as JSON with these keys:
- materialRecommendations: array of strings
- pricingSuggestions: array of strings  
- safetyWarnings: array of strings
- optimizationTips: array of strings
- estimatedSavings: number (if applicable)`;

      const completion = await openai.chat.completions.create({
        model: "gpt-4",
        messages: [{ role: "user", content: prompt }],
        temperature: 0.3,
      });

      const response = completion.choices[0]?.message?.content;
      if (response) {
        try {
          return JSON.parse(response);
        } catch {
          return this.getMockAnalysis();
        }
      }
    } catch (error) {
      console.error('Codex analysis failed:', error);
    }

    return this.getMockAnalysis();
  }

  async optimizeJob(material: string, length: number, quantity: number): Promise<CodexJobOptimization> {
    if (!this.isAvailable()) {
      return this.getMockOptimization();
    }

    try {
      const prompt = `Optimize this cutting job for maximum efficiency and cost savings:
Material: ${material}
Length: ${length} feet
Quantity: ${quantity} pieces

Provide optimization suggestions including:
- Suggested length adjustments to reduce waste
- Waste reduction percentage
- Cost savings in dollars
- Alternative materials with costs and savings

Format as JSON with these keys:
- suggestedLength: number
- wasteReduction: number (percentage)
- costSavings: number
- alternativeMaterials: array of objects with name, cost, savings`;

      const completion = await openai.chat.completions.create({
        model: "gpt-4",
        messages: [{ role: "user", content: prompt }],
        temperature: 0.2,
      });

      const response = completion.choices[0]?.message?.content;
      if (response) {
        try {
          return JSON.parse(response);
        } catch {
          return this.getMockOptimization();
        }
      }
    } catch (error) {
      console.error('Codex optimization failed:', error);
    }

    return this.getMockOptimization();
  }

  async generateSuggestions(): Promise<CodexSuggestion[]> {
    if (!this.isAvailable()) {
      return this.getMockSuggestions();
    }

    try {
      const prompt = `Generate 3-5 smart suggestions for a hardware store cut order management system. Focus on:
1. Material optimization
2. Pricing strategies
3. Safety improvements
4. Operational efficiency

Format as JSON array with objects containing:
- type: 'material' | 'pricing' | 'optimization' | 'safety'
- title: string
- description: string
- action: string (optional)
- priority: 'low' | 'medium' | 'high'`;

      const completion = await openai.chat.completions.create({
        model: "gpt-4",
        messages: [{ role: "user", content: prompt }],
        temperature: 0.4,
      });

      const response = completion.choices[0]?.message?.content;
      if (response) {
        try {
          return JSON.parse(response);
        } catch {
          return this.getMockSuggestions();
        }
      }
    } catch (error) {
      console.error('Codex suggestions failed:', error);
    }

    return this.getMockSuggestions();
  }

  async predictDemand(materialType: string, historicalData: any[]): Promise<{ predictedDemand: number; confidence: number }> {
    if (!this.isAvailable()) {
      return { predictedDemand: Math.floor(Math.random() * 100) + 50, confidence: 0.75 };
    }

    try {
      const prompt = `Based on this historical data for ${materialType}, predict demand for the next month:
${JSON.stringify(historicalData, null, 2)}

Provide prediction as JSON:
- predictedDemand: number
- confidence: number (0-1)`;

      const completion = await openai.chat.completions.create({
        model: "gpt-4",
        messages: [{ role: "user", content: prompt }],
        temperature: 0.1,
      });

      const response = completion.choices[0]?.message?.content;
      if (response) {
        try {
          return JSON.parse(response);
        } catch {
          return { predictedDemand: Math.floor(Math.random() * 100) + 50, confidence: 0.75 };
        }
      }
    } catch (error) {
      console.error('Codex demand prediction failed:', error);
    }

    return { predictedDemand: Math.floor(Math.random() * 100) + 50, confidence: 0.75 };
  }

  private getMockAnalysis(): CodexAnalysis {
    return {
      materialRecommendations: [
        "Consider using 2x6 instead of 2x4 for better structural integrity",
        "Pine is cost-effective but consider pressure-treated for outdoor use",
        "Stock up on 8ft lengths to reduce waste"
      ],
      pricingSuggestions: [
        "Implement volume discounts for orders over 100ft",
        "Consider seasonal pricing adjustments",
        "Offer package deals for multiple material types"
      ],
      safetyWarnings: [
        "Always wear safety glasses when cutting",
        "Ensure proper ventilation for treated lumber",
        "Check for nails or metal before cutting"
      ],
      optimizationTips: [
        "Cut longest pieces first to maximize remaining material",
        "Group similar cuts together to reduce setup time",
        "Consider using a cutting guide for precision"
      ],
      estimatedSavings: 15.50
    };
  }

  private getMockOptimization(): CodexJobOptimization {
    return {
      suggestedLength: 8.5,
      wasteReduction: 12,
      costSavings: 8.75,
      alternativeMaterials: [
        { name: "2x6 Pine", cost: 5.25, savings: 2.50 },
        { name: "Pressure Treated 2x4", cost: 4.20, savings: 1.20 }
      ]
    };
  }

  private getMockSuggestions(): CodexSuggestion[] {
    return [
      {
        type: 'optimization',
        title: 'Implement Smart Cutting Patterns',
        description: 'Use AI to optimize cutting patterns and reduce material waste by up to 15%',
        action: 'Enable smart cutting optimization',
        priority: 'high'
      },
      {
        type: 'pricing',
        title: 'Dynamic Pricing Strategy',
        description: 'Adjust pricing based on demand, seasonality, and competitor analysis',
        action: 'Review pricing strategy',
        priority: 'medium'
      },
      {
        type: 'safety',
        title: 'Enhanced Safety Protocols',
        description: 'Implement additional safety checks and training for cutting operations',
        action: 'Schedule safety review',
        priority: 'high'
      },
      {
        type: 'material',
        title: 'Inventory Forecasting',
        description: 'Use historical data to predict material demand and optimize stock levels',
        action: 'Enable demand forecasting',
        priority: 'medium'
      }
    ];
  }
}

export const codexService = new CodexService();
